import React, { ComponentProps } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { classnames } from 'Utils/classnames/classnames.utils';
import { upperFirst } from 'lodash';
import striptags from 'striptags';
import Image from 'Components/frameworks/image/Image';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { Title } from 'Components/frameworks/title/Title';
import { t } from 'Src/i18n';
export type SearchSuggestion = {
  comfortLevel: string;
  description: string;
  icon: string;
  image: string;
  isExclusiveCollection: boolean;
  label: string;
  link?: string;
  productColor: string;
  productId: string;
  productType: ProductType;
  queryMatches: Array<string>;
  season: string;
  subLabel1: string;
  subLabel2: string;
  type: string;
};
export type SearchSuggestionsProps = {
  autocompleteQuery: string;
  autocompleteResults: Array<SearchSuggestion>;
  clearGlobalSearch: () => void;
};
export type SearchSuggestionsBlockProps = SearchSuggestionsProps & {
  blockSuggestions: Array<SearchSuggestion>;
  blockType: string;
  isDesktop: boolean;
  onClick: (query: SearchQuery) => unknown;
};
export const SUGGESTION_TYPES = {
  ACTIVITY: 'activity',
  AREA: 'area',
  COUNTRY: 'country',
  EDITO: 'edito',
  RESORT: 'resort',
};
export const GlobalSearchSuggestionsList = ({
  blockSuggestions,
  blockType,
  isDesktop,
  onClick,
}: SearchSuggestionsBlockProps) => {
  const maxNumberOfSuggestions = !isDesktop || blockType === SUGGESTION_TYPES.EDITO ? 3 : 5;
  const onPress = (suggestion) => {
    onClick(suggestion);
  };
  return (
    <ul>
      {blockSuggestions
        .slice(0, maxNumberOfSuggestions)
        .map((suggestion) => {
          const { description, image, label, link, queryMatches, type } = suggestion;
          return {
            description,
            image,
            label: striptags(label),
            link,
            queryMatches,
            type,
          };
        })
        .map((suggestion, index) => {
          const { description, image, label, queryMatches = [], type } = suggestion;
          if (type === SUGGESTION_TYPES.EDITO) {
            const defaultPicture = {
              alt: label,
              src: image,
            };
            const stripedDescription = striptags(description || '');
            const truncatedDescription =
              stripedDescription.length > 65
                ? `${stripedDescription.substring(0, 62)}...`
                : stripedDescription;
            let decoratedDescription = truncatedDescription;
            queryMatches.forEach((queryMatch) => {
              decoratedDescription = decoratedDescription.replace(
                new RegExp(`(${queryMatch})`, 'ig'),
                "<span class='match'>$1</span>",
              );
            });
            return (
              <li
                key={label}
                className="mb-30 flex cursor-pointer"
                data-testid={`${type}-link`}
                onClick={() => onPress(suggestion)}
                onKeyUp={(event) => {
                  /* Space & Enter */
                  if (event.keyCode === 32 || event.keyCode === 13) {
                    onPress(suggestion);
                  }
                }}
              >
                {isDesktop && (
                  <div className="relative me-20 w-1/3 self-start overflow-hidden rounded-12">
                    <Image
                      {...asWidgetProps<ComponentProps<typeof Image>>({
                        altMsg: defaultPicture.alt,
                        imageUrl: defaultPicture.src,
                        key: defaultPicture.src,
                        groupKey: 'thumbnails',
                        ratioKey: ratioUtils.XL,
                        ratioDisplay: 1 / 3,
                        hasLazyLoading: false,
                      })}
                    />
                  </div>
                )}
                <div
                  className={classnames('flex-start flex flex-col overflow-hidden', {
                    'w-1/2': isDesktop,
                    'w-full': !isDesktop,
                  })}
                  role="button"
                  tabIndex={index}
                >
                  <Title
                    {...asWidgetProps<ComponentProps<typeof Title>>({
                      className: 'text-18 font-bold leading-tight',
                      hLevel: 3,
                      theme: 'none',
                    })}
                  >
                    {label}
                  </Title>
                  <div
                    className="decorator-match flex-1 pt-10 text-14 leading-tight text-black" // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: decoratedDescription,
                    }}
                  />
                  <div
                    className={classnames('text-12 font-bold text-black underline', {
                      'pt-10': !isDesktop,
                    })}
                  >
                    {upperFirst(t('common.readMoreLabel'))}
                  </div>
                </div>
              </li>
            );
          }
          return (
            <li key={label} className="mb-10 flex" data-testid={`${type}-link`}>
              <span
                role="button"
                tabIndex={index}
                className="cursor-pointer text-20 font-bold leading-tight text-black lg:text-24"
                onClick={() => onPress(suggestion)}
                onKeyUp={(event) => {
                  /* Space & Enter */
                  if (event.keyCode === 32 || event.keyCode === 13) {
                    onPress(suggestion);
                  }
                }}
              >
                {label}
              </span>
            </li>
          );
        })}
    </ul>
  );
};
